/**
 * Pure mappers between sync-API DTOs and local Dexie entities.
 *
 * Kept in a standalone module (no `db`, stores, or navigation imports) so
 * consumers like the websocket message handler can normalize a server chat
 * without pulling in the whole sync-engine module graph.
 */

import { InitChatStatus } from "@/indexedDB/types";
import type { ChatEntity } from "@/indexedDB/types";

// Local copies of the relevant enum values, inlined so this dependency-light
// mapper does not import the heavier server chat-types graph. Mirror:
//   ChatType.P2P = 1, ChatType.GROUP = 2 (server/types/chat)
//   MemberRole.MEMBER = 3               (server/types/member)
const CHAT_TYPE_P2P = 1;
const CHAT_TYPE_GROUP = 2;
const DEFAULT_MEMBER_ROLE = 3; // MemberRole.MEMBER

/**
 * Single source of truth for deriving a message row's thread-comment-count
 * badge field — `MessageEntity.commentCount`, "total comments", the value
 * `CommentCount.tsx` renders — from a server message DTO.
 *
 * Accepts the snake_case wire shape (`comment_count`, returned by every
 * GET-messages / sync read) or an already-camelCased object (`commentCount`),
 * defaulting to `null`. The Umbra/new-sync parent-row mappers
 * (`syncNewMessagesForChats`, `umbra_processMessageContent`,
 * `umbra_processParentMessage`, the last-message preview below) route through
 * this so the field can't silently drift out of one of them again — that exact
 * drift (the sync-backfill map omitted `commentCount`) was the original "no
 * badge after offline backfill" bug. (The legacy non-Umbra mappers in
 * `middleware/{chats,messages,chatsSync}.ts` still read `comment_count` inline;
 * fold them in when that path is migrated.)
 */
export function deriveCommentCount(m: {
  commentCount?: number | null;
  comment_count?: number | null;
}): number | null {
  return m.commentCount ?? m.comment_count ?? null;
}

/**
 * Normalize a raw last-message DTO into the camelCase `lastMessageCiphertext`
 * shape used for preview hydration/decryption.
 *
 * The backend last-message payload is snake_case (`attachment_meta`, `is_edited`,
 * `sender_avatar`, `client_id`, `parent_id`, `thread_id`, `comment_count`).
 * `content`/`sender`/`id`/`category` happen to share names, so the *decrypted
 * text* worked even when this was stored raw — but the edited/attachment/reply/
 * thread/avatar fields were silently dropped. Read both cases defensively.
 */
function normalizeLastMessageCiphertext(
  raw: unknown,
): ChatEntity["lastMessageCiphertext"] {
  if (raw == null || typeof raw !== "object") {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const m = raw as any;
  return {
    id: m.id,
    chatId: m.chatId ?? m.chat_id ?? "",
    sender: m.sender ?? "",
    // Read-time anonymisation marker. The chat-list DTO carries camelCase
    // `senderDeleted` (BE assembleChatEntity); message payloads use snake_case
    // `sender_deleted` — accept both, present only when true.
    ...(m.senderDeleted || m.sender_deleted ? { senderDeleted: true } : {}),
    content: m.content ?? "",
    attachmentMeta: m.attachmentMeta ?? m.attachment_meta ?? null,
    category: m.category ?? 0,
    isEdited: m.isEdited ?? m.is_edited ?? false,
    isOutgoing: m.isOutgoing ?? m.is_outgoing ?? false,
    clientId: m.clientId ?? m.client_id ?? null,
    parentId: m.parentId ?? m.parent_id ?? null,
    threadId: m.threadId ?? m.thread_id ?? null,
    commentCount: deriveCommentCount(m),
    senderAvatar: m.senderAvatar ?? m.sender_avatar ?? null,
  };
}

/**
 * Map a chat from the sync API response to the local ChatEntity shape.
 *
 * The backend's assembleChatEntity returns a flat object with fields like
 * `chatId`, `isPublic`, `secondMemberId` etc. The frontend's ChatEntity
 * uses `id` as the Dexie primary key, `type` (enum) for tab filtering,
 * `hubId` for AI chat detection, and several init/sync status fields.
 */
export function mapApiChatToEntity(apiChat: unknown): ChatEntity {
  // `apiChat` is a raw backend chat DTO (snake/camel mix, partial), NOT a local
  // ChatEntity — typed `unknown` so callers can't accidentally rely on the
  // local shape, then narrowed field-by-field below.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const src = apiChat as any;
  const now = new Date().toISOString();

  // Derive chat type: P2P if secondMemberId is set, GROUP otherwise
  // ChatType.P2P = 1, ChatType.GROUP = 2. The other-member id may arrive either
  // at top level (`src.secondMemberId`, the flat assembleChatEntity DTO) or
  // nested under `src.avatar.secondMemberId` — resolve from either.
  const secondMemberId = src.secondMemberId ?? src.avatar?.secondMemberId;
  const isP2P = !!secondMemberId;
  const chatType = isP2P ? CHAT_TYPE_P2P : CHAT_TYPE_GROUP;

  // Validate the Dexie primary key up front. `chatId || id` (NOT `??` — we want
  // to fall through an empty-string chatId to id) must resolve to a non-empty
  // string; otherwise we'd insert/update a row with an invalid key and corrupt
  // the store silently. Fail fast with a debuggable error instead.
  const id: unknown = src.chatId || src.id;
  if (typeof id !== "string" || id.length === 0) {
    throw new Error(
      "mapApiChatToEntity: chat payload is missing a usable id (chatId/id)",
    );
  }

  return {
    // Map chatId → id (backend uses chatId, Dexie primary key is id) — validated above
    id,
    name: src.name || "",
    eCSK: src.eCSK || "",
    type: src.type ?? chatType,
    hubId: src.hubId ?? null,
    // Carry attachmentId/content from the source avatar, and PERSIST the
    // resolved secondMemberId onto the avatar — the UI reads
    // `chat.avatar.secondMemberId` to render the P2P avatar and resolve the
    // other user. Omit it when absent (it's typed `string | undefined`, so a
    // `null` would leak into props expecting `string | undefined`).
    avatar: {
      attachmentId: src.avatar?.attachmentId ?? "",
      ...(src.avatar?.content !== undefined
        ? { content: src.avatar.content }
        : {}),
      ...(secondMemberId ? { secondMemberId } : {}),
    },
    description: src.description || "",
    timestamp: src.timestamp || Date.now(),
    memberCount: src.memberCount || 0,
    unreadCount: src.unreadCount || 0,
    isMuted: src.isMuted || false,
    isPinned: src.isPinned || false,
    isArchived: src.isArchived || false,
    isPublic: src.isPublic || false,
    allowThreads: src.allowThreads || false,
    canPost: src.canPost ?? DEFAULT_MEMBER_ROLE,
    pinnedMessageIds: src.pinnedMessageIds || [],
    roleAsMember: src.roleAsMember ?? {
      username: "",
      role: DEFAULT_MEMBER_ROLE,
      removed: false,
    },
    lastMessage: null, // Populated by post-sync hydration (decryptLastMessagePreviews)
    // Encrypted content for hydration — normalized so snake_case fields
    // (attachment_meta, is_edited, sender_avatar, …) survive (see helper).
    lastMessageCiphertext: normalizeLastMessageCiphertext(src.lastMessage),
    initStatus: InitChatStatus.SUCCESS,
    lastMessageTimestamp: src.lastMessageTimestamp || undefined,
    firstMessageId: src.firstMessageId || undefined,
    lastSyncedAt: src.lastSyncedAt || now,
    // Required local-only fields — default them here so EVERY consumer gets a
    // complete row. `upsertSyncedChats` overrides these (false for new chats,
    // preserved for existing), but the direct messageEventsHandler →
    // addChatToLocalDB path for unknown chats does NOT, and would otherwise
    // insert a row missing these required booleans.
    firstPhaseMsgListInit: src.firstPhaseMsgListInit ?? false,
    fullMsgListInit: src.fullMsgListInit ?? false,
  } as ChatEntity;
}
